import React, {useCallback, useEffect, useState} from "react";

import type {IAfterGuiAttachedParams, IDoesFilterPassParams,} from "ag-grid-community";
import type {CustomFilterProps} from "ag-grid-react";
import {useGridFilter} from "ag-grid-react";
import {AppEmojis, AppIds, AppNames} from "enums";
import {Button, ButtonGroup} from "react-bootstrap";

const APP_IDS = Object.values(AppIds).filter((v): v is AppIds => typeof v === 'number');

type MatchMode = 'OR' | 'AND';

type AbuseAppIdsFilterModel = {
    appIds: AppIds[];
    mode: MatchMode;
};

export const AbuseAppIdsFilter = ({model, onModelChange}: CustomFilterProps<any, any, AbuseAppIdsFilterModel>) => {
    const [closeFilter, setCloseFilter] = useState<(() => void) | undefined>();
    const [selectedAppIds, setSelectedAppIds] = useState<AppIds[]>(model?.appIds ?? []);
    const [mode, setMode] = useState<MatchMode>(model?.mode ?? 'OR');

    const doesFilterPass = useCallback((params: IDoesFilterPassParams) => {
        if (!model) {
            return true;
        }
        const ruleAppIds: AppIds[] = params.data.appIds ?? [];
        return model.mode === 'AND'
            ? model.appIds.every(item => ruleAppIds.includes(item))
            : model.appIds.some(item => ruleAppIds.includes(item));
    }, [model]);

    const afterGuiAttached = useCallback(
        ({hidePopup}: IAfterGuiAttachedParams) => {
            setCloseFilter(() => hidePopup);
        },
        [],
    );

    // register filter handlers with the grid
    useGridFilter({
        doesFilterPass,
        afterGuiAttached,
    });

    useEffect(() => {
        setSelectedAppIds(model?.appIds ?? []);
        setMode(model?.mode ?? 'OR');
    }, [model]);

    const updateModel = (appIds: AppIds[], nextMode: MatchMode) => {
        onModelChange(appIds.length ? {appIds, mode: nextMode} : null);
    };

    const onClick = () => {
        if (closeFilter) {
            closeFilter();
        }
    };

    return (
        <div className="p-3 d-flex flex-column bg-white">
            <label className={'fw-bold'}>Приложения</label>
            <div className="col-sm-10 mt-2">
                {APP_IDS.map(appId =>
                    <div key={appId} className="form-check">
                        <input className="form-check-input"
                               type="checkbox" value="" id={`app-id-${appId}`}
                               checked={selectedAppIds.includes(appId)}
                               onChange={() => {
                                   const newValue = selectedAppIds.includes(appId) ? selectedAppIds.filter(pa => pa !== appId) : [...selectedAppIds, appId];
                                   setSelectedAppIds(newValue);
                                   updateModel(newValue, mode);
                               }}/>
                        <label className="form-check-label" htmlFor={`app-id-${appId}`}>
                            {AppEmojis[appId]} {AppNames[appId]}
                        </label>
                    </div>)}
            </div>
            <label className={'fw-bold mt-3'}>Условие</label>
            <ButtonGroup size="sm" className="mt-2">
                <Button
                    variant={mode === 'OR' ? 'secondary' : 'outline-secondary'}
                    onClick={() => {
                        setMode('OR');
                        updateModel(selectedAppIds, 'OR');
                    }}>
                    Любое из выбранных
                </Button>
                <Button
                    variant={mode === 'AND' ? 'secondary' : 'outline-secondary'}
                    onClick={() => {
                        setMode('AND');
                        updateModel(selectedAppIds, 'AND');
                    }}>
                    Все выбранные
                </Button>
            </ButtonGroup>
            <Button size={'sm'} variant={'secondary'} onClick={onClick} className={'mt-3'}>Готово</Button>
        </div>
    );
};
